import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/utils';
import { ClockIcon } from './internal/icons';
import { FieldAffix } from './internal/FieldAffix';
import { PopoverSurface } from './internal/PopoverSurface';
import { TimeGrid } from './internal/TimeGrid';
import { useTimeBox } from './internal/useTimeBox';
import type { TimeBoxProps } from './types';

function footLink(label: string, onClick: () => void, tone: 'link' | 'muted') {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'hover:bg-phx-surface-2 rounded-phx h-phx-control-sm text-phx-xs cursor-pointer border-0 bg-transparent px-2 font-semibold',
        tone === 'link' ? 'text-phx-link' : 'text-phx-text-muted'
      )}
    >
      {label}
    </button>
  );
}

/**
 * Standalone time field (design's `TimeField`): a text input over a grid of hours (0–23) and stepped
 * minutes. Controlled by a canonical `"HH:mm"` string; emits `""` when cleared. State/handlers live
 * in `useTimeBox`.
 */
export function TimeBox(props: TimeBoxProps) {
  const { t } = useTranslation('dateTimeField');
  const c = useTimeBox(props);

  return (
    <>
      <FieldAffix
        {...c.chrome}
        inputId={c.inputId}
        leadingIcon={<ClockIcon />}
        placeholder="hh:mm"
        inputMode="numeric"
        text={c.inputText}
        invalid={c.invalid || Boolean(c.chrome.error)}
        error={c.errorText}
        open={c.open}
        triggerAriaLabel={c.openTimeLabel}
        anchorRef={c.anchorRef}
        inputRef={c.inputRef}
        onAffixMouseDown={c.onAffixMouseDown}
        onInputChange={c.onInputChange}
        onInputFocus={c.onInputFocus}
        onInputClick={c.onInputClick}
        onInputBlur={c.onInputBlur}
        onInputKeyDown={c.onInputKeyDown}
        onToggle={c.onToggle}
      />
      {c.open
        ? createPortal(
            <PopoverSurface
              ref={c.popRef}
              positionStyle={c.popStyle}
              data-testid="time-box-popover"
              onMouseDown={(event) => {
                if (!(event.target instanceof HTMLInputElement)) event.preventDefault();
              }}
            >
              <TimeGrid
                hour={c.hour}
                minute={c.minute}
                step={c.step}
                minuteCols={c.cols}
                hourCols={6}
                minuteExpand={c.minuteExpand}
                width={c.gridWidth}
                hourLabel={t('hour')}
                minuteLabel={t('minute')}
                selectMinuteLabel={t('selectMinute')}
                anyMinuteLabel={t('anyMinute')}
                onPickHour={c.applyHour}
                onPickMinute={c.applyMinute}
              />
              <div className="border-phx-border pt-phx-3_5 mt-2 flex items-center gap-1 border-t">
                {footLink(`↻ ${t('now')}`, c.setNow, 'link')}
                <div className="ml-auto flex gap-1">
                  {c.hasValue ? footLink(t('clear'), c.clearVal, 'muted') : null}
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={c.closePopover}
                    className="border-phx-primary bg-phx-primary text-phx-primary-fg rounded-phx h-phx-control-sm text-phx-xs cursor-pointer border px-2.5 font-semibold"
                  >
                    {t('done')}
                  </button>
                </div>
              </div>
            </PopoverSurface>,
            document.body
          )
        : null}
    </>
  );
}
